import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import WizardSelect from '../ui/WizardSelect';
import { setFlashToast } from '../../utils/flashToast';
import { listDocumentMovementOfficers, documentMoverLabel } from '../../services/taskForwarding';
import { completeWorkflowTask, submitRegularWorkflowTask } from '../../services/taskSubmission';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

export type SubmitTaskModalMode = 'submit' | 'complete';

type SubmitTaskModalProps = {
  open: boolean;
  mode?: SubmitTaskModalMode;
  caseRequestId: string;
  /** workflow_instance_stage_user_task_id */
  workflowInstanceStageUserTaskId: string;
  blockingInfoMessage?: string;
  blockedBySubsidiaryTasks?: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type LoadState = 'idle' | 'loading' | 'error';

/** Data dictionary: Intranet — Submit Task / Complete Task pop-up. */
export function SubmitTaskModal({
  open,
  mode = 'submit',
  caseRequestId,
  workflowInstanceStageUserTaskId,
  blockingInfoMessage,
  blockedBySubsidiaryTasks = false,
  onClose,
  onSuccess,
}: SubmitTaskModalProps) {
  const { t } = useTranslation();
  const isComplete = mode === 'complete';
  const modalTitle = isComplete ? 'Complete Task' : 'Submit Task';
  const confirmLabel = isComplete ? 'Complete Task' : 'Submit Task';
  const confirmingLabel = isComplete ? 'Completing…' : 'Submitting…';
  const successMessage = isComplete ? 'Task completed successfully.' : 'Task submitted successfully.';
  const failureMessage = isComplete ? 'Complete failed.' : 'Submit failed.';

  const [physicalRequired, setPhysicalRequired] = useState(true);
  const [documentMoverId, setDocumentMoverId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);

  const [moversState, setMoversState] = useState<LoadState>('idle');
  const [moverOptions, setMoverOptions] = useState<{ id: string; label: string }[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [completionRemarks, setCompletionRemarks] = useState('');

  useEffect(() => {
    if (!open) return;

    setPhysicalRequired(true);
    setDocumentMoverId('');
    setCompletionRemarks('');
    setErrors({});
    setCancelConfirmOpen(false);
    setCompleteConfirmOpen(false);
    setMoversState('loading');
    setMoverOptions([]);

    const moverController = new AbortController();
    void listDocumentMovementOfficers(moverController.signal)
      .then((r) => {
        if (moverController.signal.aborted) return;
        if (!r.success) {
          setMoversState('error');
          setFlashToast({ message: r.message || 'Failed to load document movers.', variant: 'error' });
          return;
        }
        const opts = r.data.map(documentMoverLabel).filter((x): x is { id: string; label: string } => Boolean(x));
        setMoverOptions(opts);
        setMoversState('idle');
      })
      .catch(() => {
        if (!moverController.signal.aborted) setMoversState('error');
      });

    return () => moverController.abort();
  }, [open]);

  useEffect(() => {
    if (physicalRequired) return;
    setDocumentMoverId('');
    setErrors((e) => ({ ...e, documentMoverId: '' }));
  }, [physicalRequired]);

  const moverPlaceholder = moversState === 'error' ? 'Failed to load' : 'Select Document Mover';

  const canConfirm = useMemo(() => {
    if (!caseRequestId.trim() || !workflowInstanceStageUserTaskId.trim()) return false;
    if (physicalRequired && !documentMoverId.trim()) return false;
    if (blockedBySubsidiaryTasks) return false;
    if (isComplete && !completionRemarks.trim()) return false;
    return true;
  }, [
    caseRequestId,
    workflowInstanceStageUserTaskId,
    physicalRequired,
    documentMoverId,
    blockedBySubsidiaryTasks,
    isComplete,
    completionRemarks,
  ]);

  const beginCancel = () => {
    if (submitting) return;
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    onClose();
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!caseRequestId.trim()) next.caseRequestId = 'Case request id is required.';
    if (!workflowInstanceStageUserTaskId.trim()) next.workflowInstanceStageUserTaskId = 'User task id is required.';
    if (physicalRequired && !documentMoverId.trim()) next.documentMoverId = 'Select Document Mover is required.';
    if (isComplete && !completionRemarks.trim()) next.completionRemarks = 'Completion Remarks is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const beginComplete = () => {
    if (submitting) return;
    if (!validate()) return;
    setCompleteConfirmOpen(true);
  };

  const confirmComplete = () => {
    setCompleteConfirmOpen(false);
    void submit();
  };

  const submit = async () => {
    if (submitting) return;
    if (!validate()) return;
    if (blockedBySubsidiaryTasks) {
      setFlashToast({
        message: blockingInfoMessage || 'There are pending sub tasks in this workflow stage.',
        variant: 'info',
      });
      return;
    }
    setSubmitting(true);

    const payload = {
      caseRequestId,
      workflowInstanceStageUserTaskId,
      physicalFileMovementRequired: physicalRequired,
      documentMoverId: physicalRequired ? documentMoverId : undefined,
      ...(isComplete ? { completionRemarks: completionRemarks.trim() } : {}),
    };

    const res = isComplete ? await completeWorkflowTask(payload) : await submitRegularWorkflowTask(payload);
    setSubmitting(false);

    if (!res.success) {
      setFlashToast({ message: res.message || failureMessage, variant: 'error' });
      return;
    }
    setFlashToast({ message: successMessage, variant: 'success' });
    onSuccess();
  };

  return (
    <Modal
      open={open}
      title={modalTitle}
      onClose={() => {
        beginCancel();
      }}
    >
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Discard changes?"
        message="Your changes will be lost if you cancel."
        confirmDisabled={submitting}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={confirmCancel}
      />

      <ConfirmDialog
        isOpen={completeConfirmOpen}
        title="Complete Task"
        message="Are you sure you want to complete this task?"
        confirmDisabled={submitting}
        onClose={() => setCompleteConfirmOpen(false)}
        onConfirm={confirmComplete}
      />

      {errors.caseRequestId || errors.workflowInstanceStageUserTaskId ? (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(220, 53, 69, 0.08)',
            color: 'var(--t1)',
            fontSize: 12,
            border: '1px solid rgba(220, 53, 69, 0.18)',
          }}
          role="alert"
        >
          {errors.caseRequestId || errors.workflowInstanceStageUserTaskId}
        </div>
      ) : null}

      {blockedBySubsidiaryTasks ? (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(220, 53, 69, 0.08)',
            color: 'var(--t1)',
            fontSize: 12,
            border: '1px solid rgba(220, 53, 69, 0.18)',
          }}
          role="status"
        >
          {blockingInfoMessage || 'There are pending sub tasks in this workflow stage. Please complete them to continue.'}
        </div>
      ) : null}

      <div className="cfg-assignment-form" style={{ padding: 0 }}>
        <div className="ccr-fg">
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={physicalRequired}
              disabled={submitting || blockedBySubsidiaryTasks}
              onChange={(e) => setPhysicalRequired(e.target.checked)}
            />
            Physical File Movement Required
          </label>
        </div>

        {physicalRequired ? (
          <div className="ccr-fg" style={{ marginTop: 12 }}>
            <label>
              Select Document Mover <span className="req">*</span>
            </label>
            <WizardSelect
              id={isComplete ? 'complete-task-mover' : 'submit-task-mover'}
              hasError={Boolean(errors.documentMoverId)}
              value={documentMoverId}
              placeholder={moverPlaceholder}
              options={moverOptions}
              disabled={moversState === 'error' || submitting || blockedBySubsidiaryTasks}
              loading={moversState === 'loading'}
              emptyMessage={t('common.noUsersFound')}
              onChange={(id) => {
                setDocumentMoverId(id);
                setErrors((e) => ({ ...e, documentMoverId: '' }));
              }}
            />
            <FieldError message={errors.documentMoverId} />
          </div>
        ) : null}

        {isComplete ? (
          <div className="ccr-fg" style={{ marginTop: 12 }}>
            <label>
              Completion Remarks <span className="req">*</span>
            </label>
            <textarea
              className={`ccr-inp${errors.completionRemarks ? ' inv' : ''}`}
              rows={3}
              maxLength={2000}
              placeholder="Enter completion remarks..."
              value={completionRemarks}
              disabled={submitting || blockedBySubsidiaryTasks}
              onChange={(e) => {
                setCompletionRemarks(e.target.value);
                setErrors((prev) => ({ ...prev, completionRemarks: '' }));
              }}
            />
            <FieldError message={errors.completionRemarks} />
          </div>
        ) : null}
      </div>

      <div className="cn-footer cn-footer--plain">
        <button className="mfb" type="button" disabled={submitting} onClick={beginCancel}>
          Cancel
        </button>
        <button
          className="mfb mfb-dk"
          type="button"
          disabled={submitting || !canConfirm}
          onClick={() => (isComplete ? beginComplete() : void submit())}
        >
          {submitting ? confirmingLabel : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
